import { Viewer } from "../main";
import {
  DXFFile,
  IFCFile,
  SHPFile,
} from ".";
import {
  AmbientLight,
  LineSegments,
  Mesh,
  Scene,
  Points,
  Box3,
  MathUtils,
  TextureLoader,
  Color,
  Vector3,
  BufferGeometry,
  BufferAttribute,
  Texture,
  Sprite,
  SpriteMaterial,
  LinearFilter,
  Camera
} from "three";
import { Asset, AssetType, LineworkLayerData } from "../../redux/assets-slice";
import { getCoordinateBounds, mergeBoundingBoxes, toVector2, withOpacity } from "../utilities";
import {
  LineworkBasicMaterial,
  LineworkSpriteMaterial,
  LineworkSpriteParams
} from "../rendering";
import spritePath from "../textures/sprites/point.png";
import LocalScene, {
  latitudeLongitude,
  reprojectPoints,
  webMercator
} from "../projections";
import { Feature } from "ol";
import { Vector as VectorSource } from "ol/source";
import VectorTileSource from 'ol/source/VectorTile';
import VectorTileLayer from 'ol/layer/VectorTile';
import { RayCaster } from "../ray-caster";
import geojsonvt from 'geojson-vt';
import GeoJSON from 'ol/format/GeoJSON';
import olProjection from "ol/proj/Projection";
import { Circle, Fill, Stroke, Style } from "ol/style";
import VectorLayer from "ol/layer/Vector";
import VectorImageLayer from "ol/layer/VectorImage";
import { Projection } from "../../redux/projections-slice";

const maxTileZoom = 22;
const tileSize = 4096;
const tileTolerance = 3;

const format = new GeoJSON({});

type Face = [number, number, number];
type Coordinate = [number, number, number?];

const pointTexture = new TextureLoader().load(spritePath);

// Data returned from geojson-vt is in tile pixel units
const tileProjection = new olProjection({
  code: 'TILE_PIXELS',
  units: 'tile-pixels',
  extent: [0, 0, tileSize, tileSize],
});

export enum GeomType {
  Points = "POINTS",
  Lines = "LINES",
  Polygons = "POLYGONS",
  Text = "TEXT"
}

interface FeatureResponse {
  coordinates: Coordinate[];
  polygonFaces: Face[];
  isContinuous: boolean;
  properties: any;
}

export class LineworkText extends Sprite {
  constructor(material: SpriteMaterial) {
    super(material);
  }

  checkVisibility(camera: Camera, drawDistance: number) {
    const cameraPosition2D = toVector2(camera.position);
    const meshPosition2D = toVector2(this.position);

    const distance = cameraPosition2D
      .distanceTo(meshPosition2D);

    this.visible = distance <= drawDistance;
  }
}

export class LineworkPoints extends Points {
  constructor(geometry, material) {
    super(geometry, material);
  }

  updateUniforms(drawDistance: number, fov: number, height: number) {
    const material = this.material as LineworkSpriteMaterial;
    material.screenHeight = height;
    material.fov = MathUtils.degToRad(fov);
    material.drawDistance = drawDistance;
    material.needsUpdate = true;
  }
}

export class LineworkLine extends LineSegments {
  constructor(geometry, material) {
    super(geometry, material);
  }

  updateUniforms(drawDistance: number) {
    const material = this.material as LineworkBasicMaterial;
    material.drawDistance = drawDistance;
  }
}

export class LineworkPolygon extends Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }

  updateUniforms(drawDistance: number) {
    const material = this.material as LineworkBasicMaterial;
    material.drawDistance = drawDistance;
  }
}

class MapLayer {
  private lineworkLayer: LineworkLayer;
  protected type: GeomType;
  private layer: VectorLayer<any> | VectorTileLayer | VectorImageLayer<any>;

  constructor(lineworkLayer: LineworkLayer) {
    this.lineworkLayer = lineworkLayer;
  }

  get map() {
    return this.lineworkLayer.map;
  }

  get color() {
    return this.lineworkLayer.color;
  }

  get visible() {
    return this.lineworkLayer.visible;
  }

  get style() {
    console.warn("Not implemented");
    return null;
  }

  async reprojectFeatures(features: FeatureResponse[], projection: Projection) {
    const coordinates = [];
    features.forEach(feature => {
      feature.coordinates.forEach(coordinate => {
        coordinates.push(coordinate);
      });
    });

    const reprojected = await reprojectPoints(
      coordinates, LocalScene.dataProjection, projection);

    let startIndex = 0;
    let endIndex = 0;

    features.forEach(feature => {
      endIndex += feature.coordinates.length;
      const slice = reprojected.slice(startIndex, endIndex);
      feature.coordinates = slice;
      startIndex = endIndex;
    });

    return features;
  }

  async addCoordinates(features: FeatureResponse[]) {
    let numCoordinates = 0;
    features.forEach(feature => {
      numCoordinates += feature.coordinates.length;
    });

    const useVectorTile = numCoordinates > 100000;
    const useImageLayer = numCoordinates > 10000;

    const outProjection = useVectorTile
      ? latitudeLongitude
      : webMercator;

    // Reproject all features for this layer
    features = await this.reprojectFeatures(features, outProjection);

    const geojsonFeatures = this.getFeatures(features);

    const geojsonObject = {
      type: 'FeatureCollection',
      features: geojsonFeatures,
    };

    if (useVectorTile) {
      this.generateVectorTileLayer(geojsonObject);
    } else if (useImageLayer) {
      this.generateImageLayer(geojsonObject);
    } else {
      this.generateVectorLayer(geojsonObject);
    }

    // Add new map layer
    this.map.addLayer(this.layer);
  }

  generateVectorTileLayer(geojsonObject) {
    const tileIndex = geojsonvt(geojsonObject, {
      extent: tileSize,
      maxZoom: maxTileZoom,
      tolerance: tileTolerance,
      // force pre-computed tiles up to indexMaxZoom
      indexMaxZoom: 10,
      indexMaxPoints: 0,
    });

    const source = new VectorTileSource({
      tileUrlFunction: (tileCoord) => {
        return JSON.stringify(tileCoord);
      },
      tileLoadFunction: (tile: any, url) => {
        // Get data for this tile
        const tileCoord = JSON.parse(url);
        const data = tileIndex.getTile(...tileCoord);

        // Convert to openlayers style feature collection
        const geojson = JSON.stringify({
          type: 'FeatureCollection',
          features: data ? data.features : [],
        }, replacer);

        // Convert from TILE_PIXELS to web mercator
        const features = format.readFeatures(geojson, {
          extent: source.getTileGrid().getTileCoordExtent(tileCoord),
          dataProjection: tileProjection,
          featureProjection: "EPSG:3857"
        }) as Feature<any>[];

        tile.setFeatures(features);
      }
    });

    this.layer = new VectorTileLayer({
      source: source,
      visible: this.visible,
      style: this.style,
      zIndex: 3
    });
  }

  generateImageLayer(geojsonObject) {
    const features = format.readFeatures(geojsonObject);

    const source = new VectorSource({
      wrapX: false,
      features: features as Feature<any>[],
    });

    this.layer = new VectorImageLayer({
      source: source,
      visible: this.visible,
      style: this.style,
      zIndex: 1
    });
  }

  generateVectorLayer(geojsonObject) {
    const features = format.readFeatures(geojsonObject);

    const source = new VectorSource({
      wrapX: false,
      features: features as Feature<any>[],
    });

    this.layer = new VectorLayer({
      source: source,
      visible: this.visible,
      style: this.style,
      zIndex: 2
    });
  }

  getFeatures(features: FeatureResponse[]) {
    console.warn("Not implemented");
    return [];
  }

  setVisible(state: boolean) {
    if (!this.layer) return;
    this.layer.setVisible(state);
  }

  updateStyle() {
    if (!this.layer) return;
    this.layer.setStyle(this.style);
  }

  destroy() {
    if (!this.layer) return;
    this.map.removeLayer(this.layer);
    this.layer.dispose();
  }
}

class PointsLayer extends MapLayer {
  protected type = GeomType.Points;

  private minRadius = 1;
  private maxRadius = 5;

  private minZoom = 10;
  private maxZoom = 18;

  private styleCache = {}

  get style() {
    return () => {
      const zoom = this.map.getView().getZoom();
      const styleKey = `${this.color}-${zoom}`;

      if (!this.styleCache[styleKey]) {
        const scale = (zoom - this.minZoom) / (this.maxZoom - this.minZoom);

        let pointSize = this.minRadius + scale
          * (this.maxRadius - this.minRadius);
        pointSize = Math.min(pointSize, this.maxRadius);
        pointSize = Math.max(pointSize, this.minRadius);

        this.styleCache[styleKey] = new Style({
          image: new Circle({
            radius: pointSize,
            fill: new Fill({
              color: this.color,
            }),
            stroke: new Stroke({
              color: "#000000",
              width: 1
            })
          })
        });
      }

      return this.styleCache[styleKey];
    };
  }

  getFeatures(features: FeatureResponse[]) {
    return features.map(feature => {
      const {coordinates, properties} = feature;

      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: coordinates[0]
        },
        properties: properties
      };
    });
  }
}

class LineLayer extends MapLayer {
  protected type = GeomType.Lines;

  get style() {
    return new Style({
      stroke: new Stroke({
        color: this.color,
        width: 1,
      }),
      fill: new Fill({
        color: this.color,
      })
    });
  }

  getFeatures(features: FeatureResponse[]) {
    return features.map(feature => {
      const {coordinates, properties} = feature;

      return {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: coordinates
        },
        properties: properties
      };
    });
  }
}

class PolygonLayer extends MapLayer {
  protected type = GeomType.Polygons;
  private maxVertices = 500000;

  get style() {
    return new Style({
      stroke: new Stroke({
        color: "#000000",
        width: 1,
      }),
      fill: new Fill({
        color: withOpacity(this.color, 0.5),
      })
    });
  }

  /** @private */
  getFeature(coordinates, properties) {
    return {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coordinates]
      },
      properties: properties
    };
  }

  /** @private */
  getOutline(coordinates, properties) {
    const {min, max} = getCoordinateBounds(coordinates);

    const outline = [
      [min.x, min.y, 0],
      [min.x, max.y, 0],
      [max.x, max.y, 0],
      [max.x, min.y, 0],
      [min.x, min.y, 0]
    ];

    return this.getFeature(outline, properties);
  }

  getFeatures(features: FeatureResponse[]) {
    let geojsonFeatures = [];

    for (let feature of features) {
      const {coordinates, polygonFaces, isContinuous, properties} = feature;

      if (coordinates.length > this.maxVertices) {
        console.warn("Using simplified polygon outline", feature);
        const mapFeature = this.getOutline(coordinates, properties);
        geojsonFeatures.push(mapFeature);
        continue;
      }

      const isQuad = (coordinates.length === 4)
        && (polygonFaces.length === 2);

      if (isContinuous || isQuad) {
        // Return one feature for the polygon
        const mapFeature = this.getFeature(coordinates, properties);
        geojsonFeatures.push(mapFeature);
      } else {
        // Return one feature for each face
        polygonFaces.forEach(face => {
          const faceSlice = face.map(index => coordinates[index]);
          const mapFeature = this.getFeature(faceSlice, properties);
          geojsonFeatures.push(mapFeature);
        });
      }
    };

    return geojsonFeatures;
  }
}

export class LineworkLayer {
  public id: string;
  public visible: boolean;
  public name: string;
  public file: LineworkFile
  public color: string;
  protected enabled = true;

  private mapLayers: MapLayer[] = [];
  private standardMeshes = [];
  private pickingMeshes = [];

  constructor(file: LineworkFile, layer: LineworkLayerData) {
    this.file = file;
    this.id = layer.id;
    this.visible = layer.visible;
    this.name = layer.name;
    this.color = layer.color;
  }

  get scene() {
    return this.file.scene;
  }

  get map() {
    return this.file.map;
  }

  get picker() {
    return this.file.picker;
  }

  get viewer() {
    return this.file.viewer;
  }

  get meshes() {
    return [...this.standardMeshes, ...this.pickingMeshes];
  }

  get meshColor() {
    return new Color(parseInt(this.color.slice(1), 16));
  }

  async addNewGeometry(data) {
    const keys = Object.keys(data) as GeomType[];

    for (let geomType of keys) {
      const features = data[geomType] as FeatureResponse[];

      if (geomType === GeomType.Text) {
        this.addTextData(features);
        continue;
      }

      await this.addSceneData(features, geomType);
      await this.addAerialData(features, geomType);
    }
  }

  /** @private */
  addTextData(features) {
    features.forEach(feature => {
      const {coordinates, properties} = feature;
      const offset = new Vector3(...coordinates[0]);
      const text = properties.text;

      const { texture, textScale } = generateTexture(text);

      const material = new SpriteMaterial({
        map: texture,
        transparent: false,
        alphaTest: 0.6,
        color: this.meshColor,
      });

      const standardMesh = new LineworkText(material);
      standardMesh.scale.copy(textScale);

      // Convert offset to meters
      offset.multiplyScalar(LocalScene.dataToMeters);

      // Move model to scene coordinates
      const shift = LocalScene.offsetToScene(offset);
      standardMesh.position.copy(shift);

      if (!this.enabled) return;

      // Scene view mesh
      this.standardMeshes.push(standardMesh);
      this.scene.add(standardMesh);

      // Initial visibility is false
      standardMesh.visible = false;
    });
  }

  /** @private */
  async addAerialData(features: FeatureResponse[], geomType: GeomType) {
    if (features.length === 0) return;

    let layer: MapLayer;

    if (geomType === GeomType.Points) {
      layer = new PointsLayer(this);
    } else if (geomType === GeomType.Polygons) {
      layer = new PolygonLayer(this);
    } else if (geomType === GeomType.Lines) {
      layer = new LineLayer(this);
    }

    if (!layer) return;

    await layer.addCoordinates(features);
    this.mapLayers.push(layer);
  }

  /** @private */
  async addSceneData(features: FeatureResponse[], geomType: GeomType) {
    if (features.length === 0) return;

    // Create flattened coordinates array
    const coordinates = [];
    features.forEach(feature => {
      feature.coordinates.forEach(coordinate => {
        coordinates.push(coordinate);
      });
    });

    // Not required for 2D data
    if (coordinates[0].length < 3) return;

    // Subtract offset to handle float32 issues
    const flatCoordinates = [];
    const offset = new Vector3(...coordinates[0]);
    coordinates.forEach(coordinate => {
      // Make sure height exists to avoid errors
      if (coordinate.length === 2) {
        coordinate.push(0);
      }

      flatCoordinates.push(
        coordinate[0] - offset.x,
        coordinate[1] - offset.y,
        coordinate[2] - offset.z
      );
    });

    const bufferGeometry = new BufferGeometry();
    const positions = new Float32Array(flatCoordinates);
    const gpuIndex = new Int32Array(flatCoordinates.length / 3);

    bufferGeometry.setAttribute('position', new BufferAttribute(positions, 3));
    bufferGeometry.setAttribute('gpu_index', new BufferAttribute(gpuIndex, 1));
    bufferGeometry.computeBoundingSphere();

    let standardMesh;
    let pickingMesh;

    let standardMaterial: LineworkSpriteMaterial | LineworkBasicMaterial;
    let pickingMaterial: LineworkSpriteMaterial | LineworkBasicMaterial;

    if (geomType === GeomType.Points) {
      // Create material and mesh objects
      const parameters = {
        size: 0.10,
        map: pointTexture,
        color: this.meshColor
      } as LineworkSpriteParams;

      standardMaterial = new LineworkSpriteMaterial(parameters, false);
      standardMesh = new LineworkPoints(bufferGeometry, standardMaterial);

      pickingMaterial = new LineworkSpriteMaterial(parameters, true);
      pickingMesh = new LineworkPoints(bufferGeometry, pickingMaterial);
    } else if (geomType === GeomType.Lines) {
      // Create line segments with basic material
      const indices = this.getLineIndices(features);
      bufferGeometry.setIndex(indices);

      standardMaterial = new LineworkBasicMaterial(this.meshColor, false);
      standardMesh = new LineworkLine(bufferGeometry, standardMaterial);

      pickingMaterial = new LineworkBasicMaterial(this.meshColor, true);
      pickingMesh = new LineworkLine(bufferGeometry, pickingMaterial);
    } else if (geomType === GeomType.Polygons) {
      // Create polygon with basic material
      const indices = this.getPolygonIndices(features);
      bufferGeometry.setIndex(indices);

      standardMaterial = new LineworkBasicMaterial(this.meshColor, false);
      standardMesh = new LineworkPolygon(bufferGeometry, standardMaterial);

      pickingMaterial = new LineworkBasicMaterial(this.meshColor, true);
      pickingMesh = new LineworkPolygon(bufferGeometry, pickingMaterial);
    } else {
      return;
    }

    // Convert mesh to meters
    standardMesh.scale.setScalar(LocalScene.dataToMeters);
    pickingMesh.scale.setScalar(LocalScene.dataToMeters);
    offset.multiplyScalar(LocalScene.dataToMeters);

    // Move model to scene coordinates
    const shift = LocalScene.offsetToScene(offset);
    standardMesh.position.copy(shift);
    pickingMesh.position.copy(shift);

    if (!this.enabled) return;

    // Scene view mesh
    this.standardMeshes.push(standardMesh);
    this.scene.add(standardMesh);

    // Picking scene mesh
    this.pickingMeshes.push(pickingMesh);
    this.picker.add(pickingMesh);

    // Update visibility
    standardMesh.visible = this.visible;
    pickingMesh.visible = this.visible;
  }

  /** Generate bufferGeometry index values for polygons  */
  getPolygonIndices(features: FeatureResponse[]) {
    let indices = [];
    let pointIndex = 0;

    features.forEach(segment => {
      const { coordinates, polygonFaces } = segment;

      polygonFaces.forEach(face => {
        indices.push(face[0] + pointIndex);
        indices.push(face[1] + pointIndex);
        indices.push(face[2] + pointIndex);
      });

      pointIndex += coordinates.length;
    });

    return indices;
  }

  /** Generate bufferGeometry index values for line segments  */
  getLineIndices(features: FeatureResponse[]) {
    let geomIndex = 0;
    let indices = [];

    features.forEach(segment => {
      const { coordinates } = segment;

      for (let i=0; i < coordinates.length - 1; i++) {
        indices.push(geomIndex, geomIndex + 1);
        geomIndex = geomIndex + 1;
      }

      geomIndex = geomIndex + 1;
    });

    return indices;
  }

  removeFromScene(item) {
    if (!item) return;
    this.picker.remove(item);
    this.scene.remove(item);
  }

  disposeGeometry(item) {
    if (!item) return;
    item.geometry.dispose();
    item.material.dispose();
  }

  setVisibility(state) {
    this.visible = state;

    for (let layer of this.mapLayers) {
      layer.setVisible(state);
    }

    this.standardMeshes.forEach(standardMesh => {
      standardMesh.visible = state;
    });

    this.pickingMeshes.forEach(pickingMesh => {
      pickingMesh.visible = state;
    });
  }

  setColor(color: string) {
    if (this.color === color) return;

    this.color = color;

    this.standardMeshes.forEach(mesh => {
      const material = mesh.material;
      material.color = this.meshColor;
      material.needsUpdate = true;
    });

    for (let layer of this.mapLayers) {
      layer.updateStyle();
    }
  }

  updateMaterials() {
    if (!this.visible) return;

    const drawDistance = this.file.drawDistance;

    this.meshes.forEach(mesh => {
      if (mesh instanceof LineworkText) {
        mesh.checkVisibility(this.viewer.camera, drawDistance);
      } else if (mesh instanceof LineworkPoints) {
        mesh.updateUniforms(drawDistance, this.viewer.camera.fov,
          this.viewer.height);
      } else if (mesh instanceof LineworkLine) {
        mesh.updateUniforms(drawDistance);
      } else if (mesh instanceof LineworkPolygon) {
        mesh.updateUniforms(drawDistance);
      }
    });
  }

  destroy() {
    this.enabled = false;

    this.standardMeshes.forEach(standardMesh => {
      this.removeFromScene(standardMesh);
      this.disposeGeometry(standardMesh);
    });
    this.standardMeshes = [];

    this.pickingMeshes.forEach(pickingMesh => {
      this.removeFromScene(pickingMesh);
      this.disposeGeometry(pickingMesh);
    });
    this.pickingMeshes = [];

    for (let layer of this.mapLayers) {
      layer.destroy();
    }
    this.mapLayers = [];
  }
}

export class LineworkFile {
  public id: string;
  public cadHandler: CADHandler;
  protected path: string;
  protected name: string;
  protected saved: boolean
  public boundingBox: Box3;
  public layers: LineworkLayer[] = [];

  public loaded = false;
  public enabled = true;
  public loading = false;
  public visible = false;
  public percent = 0.0;
  public error = false;

  constructor(cadHandler: CADHandler, asset: Asset) {
    this.id = asset.id;
    this.path = asset.path;
    this.name = asset.name;
    this.saved = asset.saved;
    this.cadHandler = cadHandler;
  }

  get viewer() {
    return this.cadHandler.viewer;
  }

  get scene() {
    return this.cadHandler.scene;
  }

  get map() {
    return this.cadHandler.map;
  }

  get picker() {
    return this.cadHandler.picker;
  }

  get drawDistance() {
    return this.cadHandler.drawDistance;
  }

  load() {
    console.warn("Not Implemented");
  }

  destroy() {
    console.warn("Not Implemented");
  }

  updateProperties(asset: Asset) {
    console.warn("Not Implemented");
  }

  updateMaterials() {
    this.layers.forEach(layer => {
      layer.updateMaterials();
    });
  }

  updatePercent(percent) {
    this.percent = percent;
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  validBounds(bounds: {min: number[], max: number[]}) {
    return [...bounds.min, ...bounds.max]
      .filter(x => isFinite(x)).length === 6;
  };
}

export class LineworkHandler {
  protected files: LineworkFile[] = [];
  protected cadHandler: CADHandler;

  constructor(cadHandler: CADHandler) {
    this.cadHandler = cadHandler;
  }
}

export class CADHandler {
  public scene: Scene;
  public viewer: Viewer;
  private files: LineworkFile[] = [];
  private _drawDistance = 0;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.initScene();
  }

  get picker() {
    return this.viewer.gpuPickers.default;
  }

  get camera() {
    return this.viewer.camera;
  }

  get map() {
    return this.viewer.minimap.map;
  }

  get numberOfFiles() {
    return this.files.length;
  }

  get combinedBoundingBox() {
    const boundingBoxes = this.files
      .map(file => file.boundingBox);

    return mergeBoundingBoxes(boundingBoxes);
  }

  get drawDistance() {
    if (this.viewer.orbitState) {
      return this.viewer.far;
    }

    return (this._drawDistance > 999)
      ? this.viewer.far
      : this._drawDistance;
  }

  set drawDistance(distance: number) {
    this._drawDistance = distance;
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  getByID(assetID: string) {
    return this.files.find(file => file.id === assetID);
  }

  async getProperties(event) {
    // NOTE: need different strategy once we allow shp/dxf files to return attributes

    const ifcFiles = this.files
      .filter(file => file instanceof IFCFile) as IFCFile[];

    const objects = ifcFiles
      .map(file => file.standardMesh)
      .filter(mesh => mesh?.visible);

    const raycaster = new RayCaster(this.viewer, event);
    const intersects = raycaster.intersectObjects(objects);
    if (intersects.length === 0) return [];

    const intersect = intersects[0];

    const selectedFile = ifcFiles.find(file => {
      return file.standardMesh === intersect.object;
    });

    if (!selectedFile) return [];

    const attributes = await selectedFile
      .getProperties(intersect.faceIndex);

    return attributes;
  }

  clearHighlightedFiles() {
    for (let file of this.files) {
      if (file instanceof IFCFile) {
        file.removeSubset();
      }
    }
  }

  reconcile(modelFiles: Asset[]) {
    const currentIDs = new Set(this.files.map(ifc => ifc.id));
    const newAssetIDs = new Set(modelFiles.map(ifc => ifc.id));
    const assetsToLoad = modelFiles.filter(ifc => !currentIDs.has(ifc.id));
    const filesToDelete = this.files.filter(ifc => !newAssetIDs.has(ifc.id));

    // TODO: move up to cadhandler, get rid of ifchandler / dxfhandler / shphandler

    // Delete file that need to be removed
    filesToDelete.forEach(file => {
      this.deleteFile(file);
    });

    // Add new files
    assetsToLoad.forEach(file => {
      this.addNewFile(file);
    });

    // Toggle visibility and colors for each file
    modelFiles.forEach(file => {
      this.updateProperties(file);
    });

    return assetsToLoad.length;
  }

  addNewFile(asset: Asset) {
    if (this.getByID(asset.id)) return;

    let newFile;

    if (asset.type === AssetType.IFC) {
      newFile = new IFCFile(this, asset);
    } else if (asset.type === AssetType.SHP) {
      newFile = new SHPFile(this, asset);
    } else if (asset.type === AssetType.DXF) {
      newFile = new DXFFile(this, asset);
    } else {
      return;
    }

    this.files.push(newFile);
  }

  deleteFile(file: LineworkFile) {
    file.destroy();

    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
  }

  updateProperties(asset) {
    let file = this.getByID(asset.id);
    if (!file) return;

    file.updateProperties(asset);
  }

  /** Load any file waiting for conversion */
  loadNextfile() {
    for (let file of this.files) {
      if (file.loaded || !file.enabled || !file.visible) continue;
      if (file.loading) break;

      // Load new file
      file.load();
      break;
    };
  }

  updateMaterials() {
    this.files.forEach(file => {
      file.updateMaterials();
    });
  }

  update() {
    this.loadNextfile();
    this.updateMaterials();
  }
}

const generateTexture = (text: string[]) => {
  const fontSize = 48;
  const lineThickness = 4;
  const borderThickness = 4;
  const margin = 5;

  const numLines = text.length;
  const canvas = document.createElement('canvas');
  const font = `${fontSize}px Arial`;

  const context = canvas.getContext('2d');
  context.font = font;

  // Calculate accurate text width
  let textWidth = 0;
  text.forEach(line => {
    const width = Math.ceil(context.measureText(line).width);
    textWidth = Math.max(textWidth, width);
  });

  // Update canvas width and height with final values
  canvas.height = fontSize * (numLines + 0.5) + (2 * borderThickness);
  canvas.width =  textWidth + (2 * margin) + (2 * borderThickness);
  context.font = font;

  // Draw text
  context.lineWidth = lineThickness;
  context.fillStyle = "#ffffff";
  context.strokeStyle = "#000000";

  text.forEach((line, i) => {
    const x = borderThickness + margin;
    const y = (i + 1) * fontSize + margin;

    context.strokeText(line, x, y);
    context.fillText(line, x, y);
  });

  const xScale = canvas.width / canvas.height;
  const textScale = new Vector3(xScale, 1.0, 1.0)
    .multiplyScalar(numLines)
    .divideScalar(3);

  let texture = new Texture(canvas);
  texture.flipY = true;
  texture.needsUpdate = true;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;

  return {texture, textScale};
};

const replacer = function (key, value) {
  if (!value || !value.geometry) {
    return value;
  }

  let type;
  const rawType = value.type;
  let geometry = value.geometry;
  if (rawType === 1) {
    type = 'MultiPoint';
    if (geometry.length === 1) {
      type = 'Point';
      geometry = geometry[0];
    }
  } else if (rawType === 2) {
    type = 'MultiLineString';
    if (geometry.length === 1) {
      type = 'LineString';
      geometry = geometry[0];
    }
  } else if (rawType === 3) {
    type = 'Polygon';
    if (geometry.length > 1) {
      type = 'MultiPolygon';
      geometry = [geometry];
    }
  }

  return {
    'type': 'Feature',
    'geometry': {
      'type': type,
      'coordinates': geometry,
    },
    'properties': value.tags,
  };
};